import { useLocation } from 'react-router-dom';
import React from 'react';

export const useQuery = () => {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
};
export const buildApiPath = ({
  path,
  params,
}: {
  path: string;
  params: { [key: string]: any } | null;
}) => {
  if (!params) {
    return path;
  }
  const keyValue = Object.keys(params)
    .map((key: string) => {
      return params[key] ? `${key}=${encodeURIComponent(params[key])}` : '';
    })
    .filter(Boolean);
  return `${path}?${keyValue.join('&')}`;
};
export const formatterToCaps = (text: string) => {
  if (!text) {
    return '';
  }
  const words = text.split(/(?<=[a-z])(?=[A-Z])|_/);
  const formattedText = words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  return formattedText;
};
export const validateEmails = (...emails: string[]) => {
  const regexp = new RegExp(
    /^(([^<>()\\[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  );
  return emails.filter((em) => em).find((em) => regexp.test(em));
};

export const replaceFontFamily = (text: string) => {
  const pattern = /font-family:[^;]+(;?)/gi;
  const replacement = 'font-family: Plus Jakarta Sans, sans-serif$1';
  return text ? text.replace(pattern, replacement) : '';
};

export const capitalizeFirstLetter = (str?: string): string => {
  if (!str) return '';
  return str.charAt(0)?.toUpperCase() + str.slice(1)?.toLowerCase();
};

export const formatPhoneNumber = (phoneNumberString?: string) => {
  if (phoneNumberString) {
    phoneNumberString = phoneNumberString.replace('+', '');
    const formattedPhoneNumber = phoneNumberString.replace(
      /(\d{3})(\d{3})(\d{4})/,
      '($1) $2-$3',
    );
    return formattedPhoneNumber;
  }
  return '';
};

// Mirrors betterleave-frontend's helper of the same name. Both apps parse
// location.search on their login pages; doing it natively means no dependency
// to break, after query-string's decode-uri-component went ESM-only and took
// down login on dev (#812).
export const paramsToObject = (entries: Iterable<[string, string]>) => {
  const result: Record<string, string> = {};
  for (const [key, value] of entries) {
    result[key] = value;
  }
  return result;
};

// Back-navigation origin carried in the URL as `?from=`, so it survives into a
// new tab where the Redux navigateLinkHeader is empty (collab #89).
export const withFromParam = (to: string, from: string) =>
  `${to}?${new URLSearchParams({ from }).toString()}`;

// Only an in-app absolute path is honoured: one leading `/`, not `//` or `/\`
// (protocol-relative), and no backslashes or control characters, which URL
// parsing would strip or fold into a slash. Anything else returns null.
export const safeInAppPath = (value: string | null | undefined) => {
  if (!value || value[0] !== '/') return null;
  if (value[1] === '/' || value.includes('\\')) return null;
  if ([...value].some((ch) => ch < ' ' || ch === '\x7f')) return null;
  return value;
};
